import { TaskDrawer } from '@/components/TaskDrawer';
import {
  Avatar,
  EmptyState,
  ErrorNotice,
  PriorityBadge,
  Spinner,
  StatusBadge,
  TypeBadge,
} from '@/components/ui';
import {
  useAssignSprint,
  useBacklogItems,
  useCompleteSprint,
  useCreateItem,
  useCreateSprint,
  useProject,
  useSprints,
  useStartSprint,
} from '@/hooks/queries';
import { useProjectStore } from '@/stores/project';
import { PRIORITY_META, PRIORITY_ORDER, formatDateRange } from '@/lib/design';
import type { BacklogItem, ItemType, Priority, Sprint } from '@/types/api';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

export function BacklogPage() {
  const projectId = useProjectStore((state) => state.activeProjectId);
  const { data: items, isLoading, error } = useBacklogItems(projectId);
  const { data: sprints } = useSprints(projectId);
  const { data: project } = useProject(projectId);

  const [searchParams, setSearchParams] = useSearchParams();
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [sprintFormOpen, setSprintFormOpen] = useState(false);
  const composerOpen = searchParams.get('nueva') === '1';
  const assignSprint = useAssignSprint(projectId);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5">
        <ErrorNotice>{(error as Error).message}</ErrorNotice>
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="p-6">
        <EmptyState title="Elige un proyecto en la barra lateral" />
      </div>
    );
  }

  const activeSprint = sprints?.find((s) => s.status === 'ACTIVE') ?? null;
  const plannedSprints = sprints?.filter((s) => s.status === 'PLANNED') ?? [];

  const groups: SprintGroup[] = [];
  if (activeSprint) {
    groups.push({
      sprint: activeSprint,
      tag: 'ACTIVO',
      tagTone: 'info',
      items: items?.filter((i) => i.sprintId === activeSprint.id) ?? [],
    });
  }
  for (const sprint of plannedSprints) {
    groups.push({
      sprint,
      tag: 'PLANIFICADO',
      tagTone: 'neutral',
      items: items?.filter((i) => i.sprintId === sprint.id) ?? [],
    });
  }
  groups.push({
    sprint: null,
    tag: 'SIN PLANIFICAR',
    tagTone: 'neutral',
    items: items?.filter((i) => !i.sprintId) ?? [],
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto px-6 py-5">
      {project?.canManage && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchParams({ nueva: '1' }, { replace: true })}
            className="rounded-control bg-brand px-3.5 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
          >
            + Nueva tarea
          </button>
          <button
            type="button"
            onClick={() => setSprintFormOpen((open) => !open)}
            className="rounded-control border border-line-strong bg-surface px-3.5 py-2 text-[13px] font-semibold transition-colors hover:border-line-hover"
          >
            + Nuevo sprint
          </button>
        </div>
      )}

      {sprintFormOpen && (
        <NewSprintForm projectId={projectId} onDone={() => setSprintFormOpen(false)} />
      )}

      {composerOpen && (
        <NewItemForm
          projectId={projectId}
          onDone={() => setSearchParams({}, { replace: true })}
        />
      )}

      {groups.map((group) => (
        <SprintPanel
          key={group.sprint?.id ?? 'backlog'}
          group={group}
          projectId={projectId}
          canManage={project?.canManage ?? false}
          onOpenItem={setOpenItemId}
          onDropItem={(itemId) =>
            assignSprint.mutate({ itemId, sprintId: group.sprint?.id ?? null })
          }
        />
      ))}

      {openItemId && <TaskDrawer itemId={openItemId} onClose={() => setOpenItemId(null)} />}
    </div>
  );
}

interface SprintGroup {
  sprint: Sprint | null;
  tag: string;
  tagTone: 'info' | 'neutral';
  items: BacklogItem[];
}

function SprintPanel({
  group,
  projectId,
  canManage,
  onOpenItem,
  onDropItem,
}: {
  group: SprintGroup;
  projectId: string;
  canManage: boolean;
  onOpenItem: (id: string) => void;
  onDropItem: (itemId: string) => void;
}) {
  const startSprint = useStartSprint(projectId);
  const completeSprint = useCompleteSprint(projectId);
  const [dragOver, setDragOver] = useState(false);

  const points = group.items.reduce((sum, item) => sum + (item.storyPoints ?? 0), 0);
  const name = group.sprint?.name ?? 'Backlog';

  const meta = group.sprint
    ? `${formatDateRange(group.sprint.startDate, group.sprint.endDate)} · ${group.items.length} ${
        group.items.length === 1 ? 'elemento' : 'elementos'
      }`
    : `${group.items.length} ${group.items.length === 1 ? 'elemento' : 'elementos'}`;

  return (
    <section
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const itemId = e.dataTransfer.getData('text/plain');
        // Soltar sobre el panel del que ya venia no cambia nada.
        if (itemId && !group.items.some((item) => item.id === itemId)) {
          onDropItem(itemId);
        }
      }}
      className={`overflow-hidden rounded-panel border bg-surface transition-colors ${
        dragOver ? 'border-brand bg-brand-tint' : 'border-line'
      }`}
    >
      <header className="flex items-center gap-2.5 border-b border-line-soft bg-sunken px-4 py-[13px]">
        <h2 className="font-display text-sm font-semibold">{name}</h2>
        <span
          className={`rounded-badge border px-1.5 py-px text-[10px] font-bold tracking-[.06em] ${
            group.tagTone === 'info'
              ? 'border-info-line bg-info-soft text-info-text'
              : 'border-line-strong bg-muted-surface text-muted'
          }`}
        >
          {group.tag}
        </span>
        <p className="text-[12.5px] text-subtle">{meta}</p>
        <div className="flex-1" />
        <p className="text-xs text-subtle">{points} pts</p>

        {canManage && group.sprint?.status === 'PLANNED' && (
          <button
            type="button"
            onClick={() => startSprint.mutate(group.sprint!.id)}
            disabled={startSprint.isPending}
            className="rounded-[7px] border border-line-strong bg-surface px-[11px] py-[5px] text-[12.5px] font-semibold transition-colors hover:border-line-hover disabled:opacity-50"
          >
            Iniciar sprint
          </button>
        )}
        {canManage && group.sprint?.status === 'ACTIVE' && (
          <button
            type="button"
            onClick={() => completeSprint.mutate({ sprintId: group.sprint!.id })}
            disabled={completeSprint.isPending}
            className="rounded-[7px] border border-line-strong bg-surface px-[11px] py-[5px] text-[12.5px] font-semibold transition-colors hover:border-line-hover disabled:opacity-50"
          >
            Completar sprint
          </button>
        )}
      </header>

      {group.items.length === 0 ? (
        <p className="px-4 py-6 text-center text-[13px] text-faint">
          {group.sprint
            ? 'Este sprint esta vacio. Arrastra tareas del backlog hasta aqui.'
            : 'El backlog esta vacio.'}
        </p>
      ) : (
        <ul>
          {group.items.map((item) => (
            <li key={item.id}>
              <div
                role="button"
                tabIndex={0}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', item.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => onOpenItem(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenItem(item.id);
                  }
                }}
                aria-label={`${item.key}: ${item.title}`}
                className="flex w-full cursor-pointer items-center gap-3 border-b border-line-soft px-4 py-[11px] text-left transition-colors last:border-b-0 hover:bg-canvas"
              >
                <PriorityBadge priority={item.priority} dot />
                <TypeBadge type={item.type} />
                <span className="key-badge w-16 flex-none">{item.key}</span>
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">
                  {item.title}
                </span>
                <PriorityBadge priority={item.priority} />
                <StatusBadge status={item.status} />
                {item.storyPoints !== null && (
                  <span className="points-badge">{item.storyPoints}</span>
                )}
                <Avatar user={item.assignee} size={24} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Alta de sprint. Se proponen dos semanas desde hoy, que es la duracion mas
 * habitual, pero ambas fechas se pueden cambiar.
 */
function NewSprintForm({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const createSprint = useCreateSprint(projectId);
  const { data: sprints } = useSprints(projectId);

  const today = new Date();
  const inTwoWeeks = new Date(today.getTime() + 14 * 86_400_000);

  const [name, setName] = useState(`Sprint ${(sprints?.length ?? 0) + 1}`);
  const [goal, setGoal] = useState('');
  const [startDate, setStartDate] = useState(isoDate(today));
  const [endDate, setEndDate] = useState(isoDate(inTwoWeeks));

  const invalidRange = Boolean(startDate && endDate && startDate > endDate);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || invalidRange) {
      return;
    }
    createSprint.mutate(
      {
        name: name.trim(),
        goal: goal.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-3 rounded-panel border border-line bg-surface p-4"
    >
      <div className="min-w-[180px]">
        <label htmlFor="sprint-name" className="mb-1 block text-[12.5px] font-semibold">
          Nombre del sprint
        </label>
        <input
          id="sprint-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
          className="w-full rounded-control border border-line-strong px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
      </div>

      <div className="min-w-[240px] flex-1">
        <label htmlFor="sprint-goal" className="mb-1 block text-[12.5px] font-semibold">
          Objetivo <span className="font-normal text-faint">(opcional)</span>
        </label>
        <input
          id="sprint-goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Que queremos haber cerrado al final"
          className="w-full rounded-control border border-line-strong px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
      </div>

      <div>
        <label htmlFor="sprint-start" className="mb-1 block text-[12.5px] font-semibold">
          Inicio
        </label>
        <input
          id="sprint-start"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="rounded-control border border-line-strong px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
      </div>

      <div>
        <label htmlFor="sprint-end" className="mb-1 block text-[12.5px] font-semibold">
          Fin
        </label>
        <input
          id="sprint-end"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          aria-invalid={invalidRange}
          className={`rounded-control border px-3 py-2 text-[13.5px] outline-none ${
            invalidRange ? 'border-danger' : 'border-line-strong focus:border-brand'
          }`}
        />
      </div>

      <button
        type="submit"
        disabled={createSprint.isPending || !name.trim() || invalidRange}
        className="rounded-control bg-brand px-4 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
      >
        {createSprint.isPending ? 'Creando…' : 'Crear sprint'}
      </button>
      <button
        type="button"
        onClick={onDone}
        className="rounded-control px-3 py-2 text-[13px] font-medium text-subtle transition-colors hover:text-ink"
      >
        Cancelar
      </button>

      {invalidRange && (
        <p role="alert" className="w-full text-[12.5px] text-danger">
          La fecha de inicio no puede ser posterior a la de fin.
        </p>
      )}
      {createSprint.error && (
        <p role="alert" className="w-full text-[13px] text-danger">
          {(createSprint.error as Error).message}
        </p>
      )}
    </form>
  );
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const TYPE_OPTIONS: { value: ItemType; label: string }[] = [
  { value: 'STORY', label: 'Historia' },
  { value: 'TASK', label: 'Tarea' },
  { value: 'BUG', label: 'Bug' },
  { value: 'IMPROVEMENT', label: 'Mejora' },
];

function NewItemForm({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const createItem = useCreateItem(projectId);
  const { data: project } = useProject(projectId);
  const { data: sprints } = useSprints(projectId);

  // Lo normal es crear para el sprint en curso; el backlog es la excepcion.
  const activeSprint = sprints?.find((s) => s.status === 'ACTIVE') ?? null;
  const openSprints = sprints?.filter((s) => s.status !== 'COMPLETED') ?? [];

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ItemType>('STORY');
  const [priority, setPriority] = useState<Priority>('MEDIUM');
  const [points, setPoints] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [sprintId, setSprintId] = useState<string | null>(null);
  const [sprintTouched, setSprintTouched] = useState(false);

  const effectiveSprintId = sprintTouched ? sprintId : (activeSprint?.id ?? null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) {
      return;
    }
    createItem.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        priority,
        storyPoints: points ? Number(points) : undefined,
        assigneeId: assigneeId || undefined,
        sprintId: effectiveSprintId ?? undefined,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-3 rounded-panel border border-brand-line bg-brand-tint p-4"
    >
      <div className="min-w-[280px] flex-1">
        <label htmlFor="new-title" className="mb-1 block text-[12.5px] font-semibold">
          Nueva tarea
        </label>
        <input
          id="new-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          required
          placeholder="Como usuario quiero…"
          className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
      </div>

      <div className="w-full">
        <label htmlFor="new-description" className="mb-1 block text-[12.5px] font-semibold">
          Descripcion <span className="font-normal text-faint">(opcional)</span>
        </label>
        <textarea
          id="new-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Que hay que hacer, y por que. Se puede ampliar despues desde la tarea."
          className="w-full resize-y rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
      </div>

      <div>
        <label htmlFor="new-type" className="mb-1 block text-[12.5px] font-semibold">
          Tipo
        </label>
        <select
          id="new-type"
          value={type}
          onChange={(e) => setType(e.target.value as ItemType)}
          className="rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        >
          {TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="new-priority" className="mb-1 block text-[12.5px] font-semibold">
          Prioridad
        </label>
        <select
          id="new-priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as Priority)}
          className="rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        >
          {PRIORITY_ORDER.map((option) => (
            <option key={option} value={option}>
              {PRIORITY_META[option].label}
            </option>
          ))}
        </select>
      </div>

      <div className="w-20">
        <label htmlFor="new-points" className="mb-1 block text-[12.5px] font-semibold">
          Puntos
        </label>
        <input
          id="new-points"
          type="number"
          min={0}
          max={100}
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          className="w-full rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
      </div>

      <div>
        <label htmlFor="new-assignee" className="mb-1 block text-[12.5px] font-semibold">
          Responsable
        </label>
        <select
          id="new-assignee"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          className="rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        >
          <option value="">Sin asignar</option>
          {project?.members.map((member) => (
            <option key={member.user.id} value={member.user.id}>
              {member.user.fullName}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="new-sprint" className="mb-1 block text-[12.5px] font-semibold">
          Destino
        </label>
        <select
          id="new-sprint"
          value={effectiveSprintId ?? ''}
          onChange={(e) => {
            setSprintTouched(true);
            setSprintId(e.target.value || null);
          }}
          className="rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        >
          <option value="">Backlog (sin planificar)</option>
          {openSprints.map((sprint) => (
            <option key={sprint.id} value={sprint.id}>
              {sprint.name}
              {sprint.status === 'ACTIVE' ? ' (en curso)' : ''}
            </option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        disabled={createItem.isPending || !title.trim()}
        className="rounded-control bg-brand px-4 py-2 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
      >
        {createItem.isPending ? 'Creando…' : 'Crear'}
      </button>
      <button
        type="button"
        onClick={onDone}
        className="rounded-control px-3 py-2 text-[13px] font-medium text-subtle transition-colors hover:text-ink"
      >
        Cancelar
      </button>

      {createItem.error && (
        <p role="alert" className="w-full text-[13px] text-danger">
          {(createItem.error as Error).message}
        </p>
      )}
    </form>
  );
}
